import { useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

/**
 * What the receipt upload returns: the row in the job's file store, which is
 * the reference an expense or a pending receipt records.
 */
export interface UploadedReceipt {
  fileId: string;
}

export function useUploadReceipt() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ jobId, file }: { jobId: string; file: File }): Promise<UploadedReceipt> => {
      const formData = new FormData();
      formData.append('file', file);
      // The receipt is filed into this job's Expenses folder.
      formData.append('jobId', jobId);

      // Hit the backend directly (Railway in prod) rather than the relative
      // /api path — the Vercel /api rewrite buffers the body and rejects
      // uploads over ~4.5MB with a 502 before they reach the backend.
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiBaseUrl}/uploads/receipt`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      return (await response.json()) as UploadedReceipt;
    },
    onError: (error: Error) => {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload receipt.',
        variant: 'destructive',
      });
    },
  });
}
